import * as PIXI from 'pixi.js';
import { game } from '../Game';
import { Settings } from '../Settings';
import { disableCulling } from './PIXICuller';
import { addScreenFilter, removeScreenFilter } from './ScreenFilterStack.js';
import { requestHitstop } from './Hitstop';

// Cosmetic only: Math.random, no world changes, so replays are unaffected.

const TAU = Math.PI * 2;
const rnd = (a, b) => a + Math.random() * (b - a);
const clamp01 = v => v < 0 ? 0 : v > 1 ? 1 : v;
const lerp = (a, b, t) => a + (b - a) * t;
const outCubic = t => 1 - (1 - t) ** 3;
const outBack = (t, s = 1.7) => { const u = t - 1; return 1 + (s + 1) * u * u * u + s * u * u; };
const hex = n => '#' + n.toString(16).padStart(6, '0');

const OUT = 0x1f1418;
const FIRE = { base: 0xe8381f, ember: 0xa3231a, orange: 0xff8b1e, yellow: 0xffd640, core: 0xfff9e0 };
const SMOKE = { dark: 0x56505d, mid: 0x7b7583 };
const CIRCLE_R = 250, STAR_R = 244;
const STREAK = { len: 220, w: 56 }, WEDGE = { len: 512, w: 26 };
const BASE_RADIUS = 240, BASE_OUTLINE = 5;
// Impact frames run on real frames, so slow motion does not hold the screen inverted.
const IMPACT_TIME = 0.03;
const MAX_POOLED_CIRCLES = 3000;

let TEX = null;
let impactFilter = null;
let impactLeft = 0;
let active = [];
const circlePool = [];

const bake = (w, h, draw) => {
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = h;
	const g = canvas.getContext('2d');
	g.lineJoin = 'round';
	g.lineCap = 'round';
	draw(g);
	return new PIXI.Texture({ source: new PIXI.CanvasSource({ resource: canvas, autoGenerateMipmaps: true }) });
};

export const init = () => {
	if (TEX) return;
	TEX = {};
	TEX.circle = bake(512, 512, g => { g.fillStyle = '#fff'; g.beginPath(); g.arc(256, 256, CIRCLE_R, 0, TAU); g.fill(); });
	TEX.streak = bake(256, 64, g => {
		g.beginPath(); g.moveTo(6, 32); g.lineTo(226, 8); g.arc(226, 32, 24, -Math.PI / 2, Math.PI / 2); g.closePath();
		g.fillStyle = '#fff'; g.fill(); g.lineWidth = 6; g.strokeStyle = hex(OUT); g.stroke();
	});
	TEX.wedge = bake(512, 32, g => { g.beginPath(); g.moveTo(0, 16); g.lineTo(512, 3); g.lineTo(512, 29); g.closePath(); g.fillStyle = '#fff'; g.fill(); });
	TEX.star = bake(512, 512, g => {
		const n = 13, spikes = [];
		for (let i = 0; i < n; i++) spikes.push({ a: (i / n) * TAU + rnd(-0.12, 0.12), ro: rnd(0.76, 1), ri: rnd(0.5, 0.62), da: TAU / n / 2 + rnd(-0.06, 0.06) });
		g.beginPath();
		spikes.forEach((p, i) => {
			const ox = 256 + Math.cos(p.a) * p.ro * 222, oy = 256 + Math.sin(p.a) * p.ro * 222;
			if (i) g.lineTo(ox, oy); else g.moveTo(ox, oy);
			g.lineTo(256 + Math.cos(p.a + p.da) * p.ri * 222, 256 + Math.sin(p.a + p.da) * p.ri * 222);
		});
		g.closePath();
		g.lineWidth = 18; g.strokeStyle = hex(OUT); g.stroke();
		g.fillStyle = '#fff'; g.fill();
	});
	// Inverted, high-contrast greyscale. Alpha is forced to 1 so the clear colour behind the level turns black too.
	impactFilter = new PIXI.ColorMatrixFilter();
	impactFilter.matrix = [-1.2, -2.35, -0.45, 2.5, 0, -1.2, -2.35, -0.45, 2.5, 0, -1.2, -2.35, -0.45, 2.5, 0, 0, 0, 0, 0, 1];
};

const takeCircle = parent => {
	let s = circlePool.pop();
	while (s && s.destroyed) s = circlePool.pop();
	if (!s) {
		s = new PIXI.Sprite(TEX.circle);
		s.anchor.set(0.5);
	}
	s.visible = false;
	parent.addChild(s);
	return s;
};
const releaseCircle = s => {
	if (s.destroyed) return;
	s.removeFromParent();
	if (circlePool.length < MAX_POOLED_CIRCLES) circlePool.push(s);
	else s.destroy();
};

const circ = (s, x, y, r, color) => {
	if (r <= 0.4) { s.visible = false; return; }
	s.visible = true;
	s.position.set(x, y);
	s.scale.set(r / CIRCLE_R);
	if (s._fxTint !== color) { s.tint = color; s._fxTint = color; }
};
const tone = (s, x, y, r, f, ux, uy, off, color) => {
	if (f <= 0.01) { s.visible = false; return; }
	const o = r * (1 - f) * off;
	circ(s, x + ux * o, y + uy * o, r * f, color);
};
const band = (s, cx, cy, x, y, r, f, color) => {
	if (f <= 0.01) { s.visible = false; return; }
	circ(s, cx + (x - cx) * f, cy + (y - cy) * f, r * f, color);
};

const LOBE = { x: 0, y: 0, vx: 0, vy: 0, drag: 6, rise: 0, rv: 0, rdrag: 1.6, r: 40, grow: 0.1, g0: 0.2, back: 1.6,
	delay: 0, life: 1.4, shrink: 0.4, heat: -1, cool: 1, spread: 0 };
const LAYERS = ['out', 'base', 'mid', 'ember', 'red', 'orange', 'yellow', 'core'];

// Each lobe draws one circle per layer, so the outline layer reads as one outline
// around the union and each tone layer as one flat region. Fire bands are the union
// scaled about the heat centre; as a lobe cools its red bands shrink about its own
// centre instead, so the last fire breaks up inside the smoke.
class Cloud {
	constructor(blast, parent) {
		this.blast = blast;
		this.root = new PIXI.Container();
		parent.addChild(this.root);
		this.L = {};
		for (const n of LAYERS) { const c = new PIXI.Container(); this.root.addChild(c); this.L[n] = c; }
		this.lobes = [];
		this.cx = 0; this.cy = 0;
		this.boilT = 0;
	}
	add(o) {
		const l = Object.assign({}, LOBE, o);
		l.age = -l.delay; l.j = 0; l.jx = 0; l.jy = 0; l.bv = rnd(0.86, 1.1); l.s = {};
		for (const n of LAYERS) l.s[n] = takeCircle(this.L[n]);
		this.lobes.push(l);
	}
	update(dt) {
		const { up, ow } = this.blast;
		let reroll = false;
		this.boilT += dt;
		if (this.boilT >= 0.05) { this.boilT %= 0.05; reroll = true; }
		let cx = 0, cy = 0, cr = 0, cw = 0;
		for (const l of this.lobes) {
			if (l.age < 0) continue;
			const h = l.heat - l.cool * l.age;
			if (h > 0) { const w = h * l.r; cx += l.x * w; cy += l.y * w; cr += l.r * w; cw += w; }
		}
		// Hot gas sits a little high in the ball.
		if (cw > 0) { this.cx = cx / cw + up.x * 0.18 * cr / cw; this.cy = cy / cw + up.y * 0.18 * cr / cw; }
		for (let i = this.lobes.length - 1; i >= 0; i--) {
			const l = this.lobes[i], S = l.s;
			l.age += dt;
			if (l.age < 0) continue;
			const a = l.age;
			if (a >= l.life) { for (const n of LAYERS) releaseCircle(S[n]); this.lobes.splice(i, 1); continue; }
			const d = Math.exp(-l.drag * dt);
			l.vx *= d; l.vy *= d;
			l.rv = (l.rv + l.rise * dt) * Math.exp(-l.rdrag * dt);
			l.x += (l.vx + up.x * l.rv) * dt; l.y += (l.vy + up.y * l.rv) * dt;
			let k = a < l.grow ? lerp(l.g0, 1, outBack(a / l.grow, l.back)) : 1;
			k *= 1 + l.spread * a;
			const s0 = l.life - l.shrink;
			if (a > s0) { const u = (a - s0) / l.shrink; k *= 1 - u * u; }
			if (reroll) { l.j = rnd(-0.03, 0.03); l.jx = rnd(-0.018, 0.018) * l.r; l.jy = rnd(-0.018, 0.018) * l.r; }
			const r = l.r * k * (1 + l.j);
			const x = l.x + l.jx, y = l.y + l.jy;
			if (r <= 0.4) { for (const n of LAYERS) S[n].visible = false; continue; }
			const h = l.heat - l.cool * a;
			circ(S.out, x, y, r + ow * Math.min(1, r / (ow * 2)), OUT);
			circ(S.base, x, y, r, SMOKE.dark);
			tone(S.mid, x, y, r, 0.74, -0.36, -0.93, 0.95, SMOKE.mid);
			const c = clamp01((0.55 - h) / 0.35), ox = lerp(this.cx, x, c), oy = lerp(this.cy, y, c);
			band(S.ember, ox, oy, x, y, r, clamp01(h / 0.08), FIRE.ember);
			band(S.red, ox, oy, x, y, r, clamp01((h - 0.06) / 0.16), FIRE.base);
			band(S.orange, this.cx, this.cy, x, y, r, 0.78 * l.bv * clamp01((h - 0.22) / 0.28), FIRE.orange);
			band(S.yellow, this.cx, this.cy, x, y, r, 0.56 * l.bv * clamp01((h - 0.48) / 0.25), FIRE.yellow);
			band(S.core, this.cx, this.cy, x, y, r, 0.34 * l.bv * clamp01((h - 0.72) / 0.2), FIRE.core);
		}
		return this.lobes.length > 0;
	}
	destroy() {
		for (const l of this.lobes) for (const n of LAYERS) releaseCircle(l.s[n]);
		this.lobes.length = 0;
		this.root.destroy({ children: true });
	}
}

class Sparks {
	constructor(blast, parent) {
		this.blast = blast;
		this.c = new PIXI.Container();
		parent.addChild(this.c);
		this.p = [];
	}
	add(o) {
		const s = new PIXI.Sprite(TEX.streak);
		s.anchor.set(0.88, 0.5);
		s.visible = false;
		s.tint = o.tint;
		this.c.addChild(s);
		this.p.push(Object.assign({ s, age: -o.delay }, o));
	}
	update(dt) {
		const { down } = this.blast;
		for (let i = this.p.length - 1; i >= 0; i--) {
			const p = this.p[i], s = p.s;
			p.age += dt;
			if (p.age < 0) continue;
			if (p.age >= p.life) { s.destroy(); this.p.splice(i, 1); continue; }
			const d = Math.exp(-p.drag * dt);
			p.vx = p.vx * d + down.x * p.g * dt;
			p.vy = p.vy * d + down.y * p.g * dt;
			p.x += p.vx * dt; p.y += p.vy * dt;
			const u = p.age / p.life, w = p.w * (1 - u * u);
			const len = Math.min(p.maxLen, Math.max(w * 1.6, Math.hypot(p.vx, p.vy) * 0.045));
			s.visible = true;
			s.position.set(p.x, p.y);
			s.rotation = Math.atan2(p.vy, p.vx);
			s.scale.set(len / STREAK.len, w / STREAK.w);
		}
		return this.p.length > 0;
	}
	destroy() { this.c.destroy({ children: true }); }
}

class SpeedLines {
	constructor(parent, o) {
		this.c = new PIXI.Container();
		parent.addChild(this.c);
		this.o = o;
		this.age = 0;
		this.lines = [];
		for (let i = 0; i < o.n; i++) {
			const s = new PIXI.Sprite(TEX.wedge);
			s.anchor.set(0, 0.5);
			s.tint = OUT;
			s.visible = false;
			this.c.addChild(s);
			this.lines.push({ s, a: (i / o.n) * TAU + rnd(-0.18, 0.18), k: rnd(0.7, 1.2), lag: rnd(0, 0.25) });
		}
	}
	update(dt) {
		const o = this.o;
		this.age += dt;
		if (this.age >= o.life) return false;
		for (const L of this.lines) {
			const u = clamp01((this.age / o.life) * (1 + L.lag) - L.lag * 0.4);
			const rin = lerp(o.r0, o.r1, outCubic(u)) * L.k;
			const len = o.len * L.k * Math.pow(1 - u, 0.7);
			const s = L.s;
			s.visible = len > 2;
			s.position.set(Math.cos(L.a) * rin, Math.sin(L.a) * rin);
			s.rotation = L.a;
			s.scale.set(len / WEDGE.len, (o.w * (1 - u * 0.5)) / WEDGE.w);
		}
		return true;
	}
	destroy() { this.c.destroy({ children: true }); }
}

// A white starburst that pops in right after the impact frames, timed in real frames.
class Starburst {
	constructor(parent, r) {
		this.s = new PIXI.Sprite(TEX.star);
		this.s.anchor.set(0.5);
		this.s.rotation = rnd(0, TAU);
		this.s.visible = false;
		parent.addChild(this.s);
		this.base = r / STAR_R;
		this.age = -IMPACT_TIME;
	}
	update(dt, frameDt) {
		this.age += frameDt;
		const a = this.age;
		if (a < 0) return true;
		if (a >= 0.1) return false;
		let k = a < 0.03 ? lerp(0.6, 1, outBack(a / 0.03, 1.7)) : 1;
		if (a > 0.08) { const u = (a - 0.08) / 0.02; k *= 1 - u * u; }
		this.s.visible = true;
		this.s.scale.set(this.base * k);
		return true;
	}
	destroy() { this.s.destroy(); }
}

const fireball = (blast, cloud, R, n, puffs) => {
	const S = blast.S, life = 1.8, cool = 1.25, rise = 260 * S, drag = 7;
	for (let i = 0; i < 3; i++) {
		const a = (i / 3) * TAU + rnd(-0.5, 0.5), d = R * rnd(0.1, 0.22);
		cloud.add({ x: Math.cos(a) * d, y: Math.sin(a) * d, r: R * rnd(0.38, 0.46), heat: 1.12 + rnd(-0.06, 0.06), cool: cool * rnd(0.92, 1.08),
			grow: 0.08, back: 1.8, life: life * rnd(0.92, 1.06), shrink: 0.55, rise, spread: 0.1 });
	}
	for (let i = 0; i < n; i++) {
		const a = (i / n) * TAU + rnd(-0.28, 0.28), d = R * rnd(0.36, 0.62);
		const tx = Math.cos(a) * d, ty = Math.sin(a) * d;
		cloud.add({ x: tx * 0.2, y: ty * 0.2, vx: tx * 0.8 * drag, vy: ty * 0.8 * drag, drag, r: R * rnd(0.32, 0.48),
			heat: 1.06 + rnd(-0.14, 0.1), cool: cool * rnd(0.95, 1.2), grow: rnd(0.06, 0.11), back: 1.6, life: life * rnd(0.72, 0.98),
			shrink: rnd(0.35, 0.55), rise: rise * rnd(0.8, 1.3), spread: 0.14, delay: rnd(0, 0.025) });
	}
	for (let i = 0; i < puffs; i++) {
		const a = rnd(0, TAU), d = R * rnd(0.72, 1.0);
		const tx = Math.cos(a) * d, ty = Math.sin(a) * d;
		cloud.add({ x: tx * 0.3, y: ty * 0.3, vx: tx * 0.7 * drag, vy: ty * 0.7 * drag, drag, r: R * rnd(0.15, 0.24),
			heat: rnd(0.55, 0.8), cool: cool * rnd(1.4, 1.9), grow: 0.08, back: 1.4, life: life * rnd(0.4, 0.6), shrink: 0.3,
			rise: rise * 0.8, spread: 0.1, delay: rnd(0.01, 0.05) });
	}
};

const SPARK_TINTS = [FIRE.yellow, FIRE.core, FIRE.orange, FIRE.yellow];

class Blast {
	constructor(root, S, lite, gx, gy) {
		this.root = root;
		this.S = S;
		this.ow = BASE_OUTLINE * Math.sqrt(S);
		const gl = Math.hypot(gx, gy);
		this.down = gl > 1e-6 ? { x: gx / gl, y: gy / gl } : { x: 0, y: 0 };
		this.up = { x: -this.down.x, y: -this.down.y };
		this.mid = new PIXI.Container();
		this.front = new PIXI.Container();
		root.addChild(this.mid, this.front);
		this.items = [];
		this.time = 0;

		const R = BASE_RADIUS * S * 1.05;
		this.items.push(new Starburst(this.front, R * 1.5));
		this.items.push(new SpeedLines(this.front, { n: lite ? 12 : 26, r0: R * 0.5, r1: R * 2.2, len: R * 1.6, w: 16 * Math.sqrt(S), life: 0.3 }));
		this.pending = () => {
			const cloud = new Cloud(this, this.mid);
			fireball(this, cloud, R, lite ? 6 : 11, lite ? 2 : 6);
			this.items.push(cloud);
			if (lite) return;
			const sparks = new Sparks(this, this.front);
			for (let i = 0; i < 14; i++) {
				const a = Math.atan2(this.up.y || -1, this.up.x) + rnd(-2.2, 2.2), sp = rnd(700, 1400) * Math.sqrt(S);
				sparks.add({ x: Math.cos(a) * R * 0.2, y: Math.sin(a) * R * 0.2, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
					life: rnd(0.25, 0.55), w: rnd(5, 9) * Math.sqrt(S), g: 1500 * S, drag: 1.5, delay: rnd(0, 0.03),
					tint: SPARK_TINTS[(Math.random() * SPARK_TINTS.length) | 0], maxLen: 160 * Math.sqrt(S) });
			}
			this.items.push(sparks);
		};
	}
	update(dt, frameDt) {
		this.time += frameDt;
		if (this.pending && this.time >= IMPACT_TIME + 0.01) { this.pending(); this.pending = null; }
		for (let i = 0; i < this.items.length; i++) {
			if (!this.items[i].update(dt, frameDt)) { this.items[i].destroy(); this.items.splice(i--, 1); }
		}
		return this.items.length > 0 || this.pending;
	}
	destroy() {
		for (const item of this.items) item.destroy();
		this.items.length = 0;
		if (!this.root.destroyed) this.root.destroy({ children: true });
	}
}

export const play = (body, position, scale = 1, power = 2500) => {
	if (active.length >= Settings.maxExplosionEffects * 2) return;
	init();
	const lite = active.length >= Settings.maxExplosionEffects;

	const root = new PIXI.Container();
	root.position.set(position.x * Settings.PTM, position.y * Settings.PTM);
	const target = body?.myTexture || body?.mySprite;
	const parent = target?.parent;
	if (parent && !parent.destroyed) parent.addChildAt(root, parent.getChildIndex(target) + 1);
	else game.editor.textures.addChild(root);
	disableCulling(root);

	const gravity = game.world.GetGravity();
	active.push(new Blast(root, scale, lite, gravity.get_x(), gravity.get_y()));

	// The inverted frame is the one the hitstop holds on screen.
	if (!lite && requestHitstop(Math.round(Math.min(8, Math.max(3, 2 + power / 1500))), position, { screen: true })) {
		impactLeft = IMPACT_TIME;
		addScreenFilter(impactFilter);
	}
};

export const update = () => {
	const frameDt = Settings.timeStep / 1000;
	const dt = game.editor.deltaTime / 1000;
	if (impactLeft > 0) {
		impactLeft -= frameDt;
		if (impactLeft <= 0) removeScreenFilter(impactFilter);
	}
	for (let i = 0; i < active.length; i++) {
		const blast = active[i];
		if (blast.root.destroyed || !blast.update(dt, frameDt)) { blast.destroy(); active.splice(i--, 1); }
	}
};

export const reset = () => {
	for (const blast of active) blast.destroy();
	active = [];
	impactLeft = 0;
	if (impactFilter) removeScreenFilter(impactFilter);
};
